/**
 * Payment Records React Hooks
 * Hooks for fetching payment records from Firestore
 * Follows the architecture pattern from useOrders.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentService } from '../services/payments';
import { paymentsApiService, type INeutralPostAuthResult } from '../services/paymentsApi';
import { ordersApi, type CustomerPaymentSummary } from '../services/ordersApi';
import { createLogger } from '../utils/logger';

const logger = createLogger('usePaymentRecords');

/**
 * Defines the query keys used for payment record data
 */
const paymentRecordKeys = {
    all: ['paymentRecords'] as const,
    lists: () => [...paymentRecordKeys.all, 'list'] as const,
    byRetref: (retref: string) => [...paymentRecordKeys.lists(), { retref }] as const,
    byOrderGuid: (orderGuid: string) => [...paymentRecordKeys.lists(), { orderGuid }] as const,
    // Customer-facing token-gated summary (checkout confirmation) — keyed
    // separately from the staff by-order list so the two caches never collide.
    byOrderToken: (orderGuid: string, tenantId: number, locationNumericId: number) =>
        [...paymentRecordKeys.lists(), 'token', { orderGuid, tenantId, restaurantId: locationNumericId }] as const,
    byRestaurant: (tenantId: number, locationNumericId: number) =>
        [...paymentRecordKeys.lists(), 'restaurant', { tenantId, restaurantId: locationNumericId }] as const,
    byBatch: (tenantId: number, locationNumericId: number, batchId: string) =>
        [...paymentRecordKeys.lists(), 'batch', { tenantId, restaurantId: locationNumericId, batchId }] as const,
};

export type { CustomerPaymentSummary };

/**
 * Hook to fetch a payment record by retref (the processor reference number)
 * The retref/transactionRef is the single source of truth for payment transactions
 * @param retref The processor reference number
 * @param tenantDocId The tenant Firestore document ID
 * @param locationDocId The restaurant Firestore document ID
 * @returns Query result with payment record or null
 */
export const useGetPaymentByRetref = (
    retref: string | null,
    tenantDocId: string | null,
    locationDocId: string | null
) => {
    return useQuery({
        queryKey: retref ? paymentRecordKeys.byRetref(retref) : ['paymentRecords', 'null'],
        queryFn: async () => {
            if (!retref || !tenantDocId || !locationDocId) {
                logger.debug('Missing required parameters for payment lookup');
                return null;
            }
            return paymentService.getPaymentByRetref(retref, tenantDocId, locationDocId);
        },
        enabled: !!retref && !!tenantDocId && !!locationDocId,
        staleTime: 1000 * 60 * 5, // 5 minutes - payment records are immutable
        gcTime: 1000 * 60 * 30, // 30 minutes
    });
};

/**
 * Hook to fetch payment records by order GUID
 * Returns all payment records linked to an order (supports split payments)
 * @param orderGuid The order GUID
 * @param tenantDocId The tenant Firestore document ID
 * @param locationDocId The restaurant Firestore document ID
 * @returns Query result with array of payment records
 */
export const useGetPaymentsByOrderGuid = (
    orderGuid: string | null,
    tenantDocId: string | null,
    locationDocId: string | null
) => {
    return useQuery({
        queryKey: orderGuid ? paymentRecordKeys.byOrderGuid(orderGuid) : ['paymentRecords', 'null-guid'],
        queryFn: async () => {
            if (!orderGuid || !tenantDocId || !locationDocId) {
                logger.debug('Missing required parameters for payment lookup by order GUID');
                return [];
            }
            return paymentService.getPaymentsByOrderGuid(orderGuid, tenantDocId, locationDocId);
        },
        enabled: !!orderGuid && !!tenantDocId && !!locationDocId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
    });
};

/**
 * CUSTOMER-facing payment summary for one order via the token-gated route
 * `GET /orders/by-guid/:guid/payments`. Backs the checkout confirmation page:
 * the authoritative amount charged (survives refresh) + card brand/last4, which
 * a checkout customer cannot read from Firestore or the staff by-order route.
 *
 * Access is authorized by the durable order-access token (or order owner /
 * staff). Only `enabled` when the order GUID + numeric tenant/restaurant scope
 * are present. Payments are effectively immutable post-checkout, so we cache
 * for a few minutes.
 */
export const useOrderPaymentSummary = (
    orderGuid: string | null | undefined,
    scope: {
        tenantId: number;
        restaurantId: number;
        accessToken?: string;
    },
) => {
    const { tenantId, restaurantId: locationNumericId, accessToken } = scope;
    return useQuery<CustomerPaymentSummary[]>({
        queryKey: orderGuid
            ? paymentRecordKeys.byOrderToken(orderGuid, tenantId, locationNumericId)
            : ['paymentRecords', 'null-order-token'],
        queryFn: () =>
            ordersApi.getOrderPaymentsByToken(orderGuid!, tenantId, locationNumericId, accessToken),
        enabled: !!orderGuid && !!tenantId && !!locationNumericId,
        staleTime: 1000 * 60 * 2, // 2 minutes — payment records are ~immutable
        gcTime: 1000 * 60 * 30,
    });
};

/**
 * Hook to fetch payment records by order GUID using numeric tenant/restaurant IDs.
 * Resolves Firestore doc IDs internally via the service layer.
 */
export const useOrderPayments = (
    orderGuid: string | null,
    tenantId: number,
    locationNumericId: number,
) => {
    return useQuery({
        queryKey: orderGuid
            ? [...paymentRecordKeys.byOrderGuid(orderGuid), { tenantId, restaurantId: locationNumericId }]
            : ['paymentRecords', 'null-order'],
        queryFn: () => paymentService.getPaymentsByOrder(orderGuid!, tenantId, locationNumericId),
        enabled: !!orderGuid && !!tenantId && !!locationNumericId,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
};

/**
 * Hook: ALL recent payments for a restaurant (newest first), INCLUDING
 * standalone / orderless quick-charges. Backs the management Transactions list.
 * Resolves doc IDs internally from numeric ids.
 */
export const useLocationPayments = (
    tenantId: number,
    locationNumericId: number,
    maxResults = 200,
) => {
    return useQuery({
        queryKey: paymentRecordKeys.byRestaurant(tenantId, locationNumericId),
        queryFn: () => paymentService.getPaymentsByRestaurantIds(tenantId, locationNumericId, maxResults),
        enabled: !!tenantId && !!locationNumericId,
        staleTime: 1000 * 30, // 30s — payments mutate (void/refund/settle)
        gcTime: 1000 * 60 * 10,
    });
};

/**
 * Hook: payments in a settlement batch (by providerBatchId) — batch→txns
 * drill-down. Empty until a batchId is supplied.
 */
export const usePaymentsByBatch = (
    tenantId: number,
    locationNumericId: number,
    batchId: string | null,
) => {
    return useQuery({
        queryKey: batchId
            ? paymentRecordKeys.byBatch(tenantId, locationNumericId, batchId)
            : ['paymentRecords', 'null-batch'],
        queryFn: () => paymentService.getPaymentsByBatchIds(tenantId, locationNumericId, batchId!),
        enabled: !!tenantId && !!locationNumericId && !!batchId,
        staleTime: 1000 * 30,
        gcTime: 1000 * 60 * 10,
    });
};

/**
 * Manager void of a captured/authorized payment (pre-settlement), provider-
 * agnostic. On success, invalidate all payment lists so the row reflects its
 * new 'voided' status. Throws on failure so the caller can surface it.
 */
export const useVoidTransaction = () => {
    const queryClient = useQueryClient();
    return useMutation<
        INeutralPostAuthResult,
        Error,
        { retref: string; tenantId: number; restaurantId: number; amount?: string; voidReasonText?: string }
    >({
        mutationFn: (vars) => paymentsApiService.voidPayment(vars),
        onSuccess: (data) => {
            logger.info('Void result', { retref: data.retref, respstat: data.respstat });
            queryClient.invalidateQueries({ queryKey: paymentRecordKeys.all });
        },
    });
};

/**
 * Manager refund of a settled payment (full or partial), provider-agnostic.
 */
export const useRefundTransaction = () => {
    const queryClient = useQueryClient();
    return useMutation<
        INeutralPostAuthResult,
        Error,
        { retref: string; tenantId: number; restaurantId: number; amount?: string }
    >({
        mutationFn: (vars) => paymentsApiService.refundPayment(vars),
        onSuccess: (data) => {
            logger.info('Refund result', { retref: data.retref, respstat: data.respstat });
            queryClient.invalidateQueries({ queryKey: paymentRecordKeys.all });
        },
    });
};

export { paymentRecordKeys };
